/**
 * Briefs Service
 *
 * HttpClient-aware API layer. Methods take an ``HttpClient`` first argument
 * and branch URL construction on ``http.context.kind``. Portal context uses
 * ``/api/client-portal/briefs/*`` so the client id is derived from the JWT;
 * admin context uses ``/api/clients/{clientId}/briefs`` and ``/api/briefs/*``.
 */

import { apiClient } from "@/services/apiClient";
import type { HttpClient } from "@/core/http/types";
import {
  Brief,
  BriefListItem,
  BriefNote,
  BriefsResponse,
  BriefNotesResponse,
  PortalBriefListItem,
  CreateBriefRequest,
  UpdateBriefRequest,
  ShareBriefResponse,
  AddNoteRequest,
  GenerateContextResponse,
} from "../types";

function handleError(error: unknown, defaultMessage: string): never {
  console.error(`BriefsService Error: ${defaultMessage}`, error);
  if (error instanceof Error) throw error;
  throw new Error(defaultMessage);
}

export const briefsService = {
  async getBriefs(http: HttpClient, clientId: string): Promise<BriefListItem[]> {
    try {
      const response = await http.get<BriefsResponse>(`/api/clients/${clientId}/briefs`);
      return response.briefs;
    } catch (error) {
      handleError(error, "Failed to fetch briefs");
    }
  },

  /** Portal-only: the creator production queue (CAPTURING + EDITING). */
  async getPortalBriefs(http: HttpClient): Promise<PortalBriefListItem[]> {
    try {
      const response = await http.get<{ briefs: PortalBriefListItem[] }>(
        "/api/client-portal/briefs"
      );
      return response.briefs;
    } catch (error) {
      handleError(error, "Failed to fetch briefs");
    }
  },

  async getBrief(http: HttpClient, briefId: string): Promise<Brief> {
    try {
      if (http.context?.kind === "portal") {
        const response = await http.get<{ brief: Brief }>(
          `/api/client-portal/briefs/${briefId}`
        );
        return response.brief;
      }
      return await http.get<Brief>(`/api/briefs/${briefId}`);
    } catch (error) {
      handleError(error, "Failed to fetch brief");
    }
  },

  async createBrief(
    http: HttpClient,
    clientId: string,
    data: CreateBriefRequest
  ): Promise<Brief> {
    try {
      return await http.post<Brief>(`/api/clients/${clientId}/briefs`, data);
    } catch (error) {
      handleError(error, "Failed to create brief");
    }
  },

  async updateBrief(
    http: HttpClient,
    briefId: string,
    data: UpdateBriefRequest
  ): Promise<Brief> {
    try {
      return await http.patch<Brief>(`/api/briefs/${briefId}`, data);
    } catch (error) {
      handleError(error, "Failed to update brief");
    }
  },

  async deleteBrief(http: HttpClient, briefId: string): Promise<void> {
    try {
      await http.delete(`/api/briefs/${briefId}`);
    } catch (error) {
      handleError(error, "Failed to delete brief");
    }
  },

  /**
   * Mint a photographer magic link for this brief.
   * Backend transitions the brief to ``CAPTURING`` and returns a fresh
   * 7-day link each call (older links remain valid until natural expiry).
   */
  async shareBrief(http: HttpClient, briefId: string): Promise<ShareBriefResponse> {
    try {
      return await http.post<ShareBriefResponse>(`/api/briefs/${briefId}/share`);
    } catch (error) {
      handleError(error, "Failed to generate share link");
    }
  },

  async addIdeaToBrief(
    http: HttpClient,
    briefId: string,
    ideaId: string
  ): Promise<Brief> {
    try {
      return await http.post<Brief>(`/api/briefs/${briefId}/ideas/${ideaId}`);
    } catch (error) {
      handleError(error, "Failed to add idea to brief");
    }
  },

  async removeIdeaFromBrief(
    http: HttpClient,
    briefId: string,
    ideaId: string
  ): Promise<Brief> {
    try {
      return await http.delete<Brief>(`/api/briefs/${briefId}/ideas/${ideaId}`);
    } catch (error) {
      handleError(error, "Failed to remove idea from brief");
    }
  },

  async getBriefNotes(http: HttpClient, briefId: string): Promise<BriefNote[]> {
    try {
      const response = await http.get<BriefNotesResponse>(
        `/api/briefs/${briefId}/notes`
      );
      return response.notes;
    } catch (error) {
      handleError(error, "Failed to fetch brief notes");
    }
  },

  async addBriefNote(
    http: HttpClient,
    briefId: string,
    data: AddNoteRequest
  ): Promise<BriefNote> {
    try {
      // Portal context POSTs to the JWT-protected portal route; admin uses /api/briefs/:id/notes.
      const path =
        http.context.kind === "portal"
          ? `/api/client-portal/briefs/${briefId}/notes`
          : `/api/briefs/${briefId}/notes`;
      return await http.post<BriefNote>(path, data);
    } catch (error) {
      handleError(error, "Failed to add note to brief");
    }
  },

  async updateBriefNote(
    http: HttpClient,
    briefId: string,
    noteId: string,
    data: AddNoteRequest
  ): Promise<BriefNote> {
    try {
      return await http.put<BriefNote>(`/api/briefs/${briefId}/notes/${noteId}`, data);
    } catch (error) {
      handleError(error, "Failed to update note");
    }
  },

  async deleteBriefNote(
    http: HttpClient,
    briefId: string,
    noteId: string
  ): Promise<void> {
    try {
      await http.delete(`/api/briefs/${briefId}/notes/${noteId}`);
    } catch (error) {
      handleError(error, "Failed to delete note");
    }
  },

  async generateContext(
    http: HttpClient,
    briefId: string
  ): Promise<GenerateContextResponse> {
    try {
      return await http.post<GenerateContextResponse>(
        `/api/briefs/${briefId}/generate-context`
      );
    } catch (error) {
      handleError(error, "Failed to generate context");
    }
  },

  /**
   * Pin a calendar event to a brief via the polymorphic link API.
   * Returns the link id so the caller can unpin without reloading.
   */
  async pinCalendarEvent(
    http: HttpClient,
    briefId: string,
    calendarEventId: string
  ): Promise<{ id: string }> {
    try {
      const response = await http.post<{ link: { id: string } }>("/api/links", {
        sourceType: "brief",
        sourceId: briefId,
        targetType: "calendar_event",
        targetId: calendarEventId,
      });
      return { id: response.link.id };
    } catch (error) {
      handleError(error, "Failed to pin calendar event");
    }
  },

  /**
   * Unpin a calendar event from a brief. Looks up the link id via the
   * generic ``GET /api/links`` and deletes it.
   */
  async unpinCalendarEvent(
    http: HttpClient,
    briefId: string,
    calendarEventId: string
  ): Promise<void> {
    try {
      const response = await http.get<{
        links: Array<{ id: string; targetId: string; targetType: string }>;
      }>(
        `/api/links?source_type=brief&source_id=${encodeURIComponent(briefId)}&target_type=calendar_event`
      );
      const link = response.links.find((l) => l.targetId === calendarEventId);
      if (!link) return;
      await http.delete(`/api/links/${link.id}`);
    } catch (error) {
      handleError(error, "Failed to unpin calendar event");
    }
  },
};

/**
 * Legacy default export for non-React callers. Binds to ``apiClient``.
 * Prefer ``useHttpClient()`` + ``briefsService`` methods inside components.
 */
const legacyAdmin = {
  getBriefs: (c: string) => briefsService.getBriefs(apiClient as unknown as HttpClient, c),
  getBrief: (id: string) => briefsService.getBrief(apiClient as unknown as HttpClient, id),
  createBrief: (c: string, d: CreateBriefRequest) =>
    briefsService.createBrief(apiClient as unknown as HttpClient, c, d),
  updateBrief: (id: string, d: UpdateBriefRequest) =>
    briefsService.updateBrief(apiClient as unknown as HttpClient, id, d),
  deleteBrief: (id: string) =>
    briefsService.deleteBrief(apiClient as unknown as HttpClient, id),
  shareBrief: (id: string) =>
    briefsService.shareBrief(apiClient as unknown as HttpClient, id),
  addIdeaToBrief: (b: string, i: string) =>
    briefsService.addIdeaToBrief(apiClient as unknown as HttpClient, b, i),
  removeIdeaFromBrief: (b: string, i: string) =>
    briefsService.removeIdeaFromBrief(apiClient as unknown as HttpClient, b, i),
  getBriefNotes: (id: string) =>
    briefsService.getBriefNotes(apiClient as unknown as HttpClient, id),
  addBriefNote: (id: string, d: AddNoteRequest) =>
    briefsService.addBriefNote(apiClient as unknown as HttpClient, id, d),
  updateBriefNote: (b: string, n: string, d: AddNoteRequest) =>
    briefsService.updateBriefNote(apiClient as unknown as HttpClient, b, n, d),
  deleteBriefNote: (b: string, n: string) =>
    briefsService.deleteBriefNote(apiClient as unknown as HttpClient, b, n),
  generateContext: (id: string) =>
    briefsService.generateContext(apiClient as unknown as HttpClient, id),
};

export default legacyAdmin;
